/*
 * ******************************************************************
 * Copyright (c) 2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { AfterViewInit, Directive, ElementRef, Input, isDevMode, OnChanges, Renderer2, SimpleChanges } from "@angular/core";

/*
 * clr-vertical-nav-group's collapse/expand <button class="nav-group-trigger"> only gets an
 * accessible name from whatever lands in its unnamed ng-content slot. When the group's visible
 * label has to live inside the [clrVerticalNavLink] anchor instead (so it stays clickable and
 * navigates), nothing is left for that slot and the trigger button ends up name-less. Content
 * projection can't route a label into both places at once, so this directive sets aria-label on
 * the rendered button directly.
 */
// @formatter:off
@Directive({
    standalone: false,
    selector: "[dr-nav-group-trigger-label]"
})
// @formatter:on
export class DrNavGroupTriggerLabelDirective implements AfterViewInit, OnChanges {

    @Input("dr-nav-group-trigger-label") label: string;

    constructor(private elementRef: ElementRef<HTMLElement>,
                private renderer: Renderer2) {
    }

    ngAfterViewInit(): void {
        this.applyLabel();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes.label.firstChange) {
            this.applyLabel();
        }
    }

    private applyLabel(): void {
        if (!this.label) {
            return;
        }
        // Coupled to clr-vertical-nav-group's internal markup (button.nav-group-trigger).
        // If a Clarity upgrade renames/restructures it, this selector silently stops matching
        // and the trigger loses its accessible name again - the warning below is the tripwire.
        const trigger: HTMLElement = this.elementRef.nativeElement.querySelector(".nav-group-trigger");
        if (trigger) {
            this.renderer.setAttribute(trigger, "aria-label", this.label);
        } else if (isDevMode()) {
            console.warn(`[dr-nav-group-trigger-label] could not find ".nav-group-trigger" inside `
                + `<clr-vertical-nav-group> to label "${this.label}" - check clr-vertical-nav-group's `
                + `internal markup after a Clarity upgrade.`);
        }
    }
}
